import { computed } from 'vue';
import { useTheme } from 'vuetify';
import { chartTheme } from './chartTheme.js';
import { THEME_LIGHT, THEME_DARK } from './vuetifyThemes.js';

/**
 * Reactive chart colours bound to Vuetify's CURRENT theme — the replacement for
 * the DOM sniffing the PNG-export helper used to do, and for threading a
 * `chartFontColor` prop down through every page.
 *
 * Must be called from `setup()` (it calls `useTheme()`), and only inside an app
 * that installed Vuetify — i.e. from a chart component, never from the store.
 * The store keeps using the framework-free `chartTheme(state.theme)`.
 *
 * Carries `mode` on top of `chartTheme(mode)`'s keys, for the callers that have to
 * hand the mode itself to a framework-free helper — `downloadChartAsPNG(chart,
 * name, series, { mode })` in `@rdrive/helper-chart-export` is the reason it exists.
 *
 * Since S11 it also carries the five mode-independent chart entries
 * `chartTheme()` exposes — `series`, `seriesFallback`, `axisPointerLabel`,
 * `dataZoom`, `loadingOverlay` — so a chart component reads every colour it
 * needs from this one computed, and a theme toggle repaints without a reload.
 *
 * @returns {import('vue').ComputedRef<{ mode: string, fontColor: string,
 *   background: string, splitLineColor: string, palette: string[],
 *   othersColor: string, unassignedColor: string, series: string[], seriesFallback: string,
 *   axisPointerLabel: string,
 *   dataZoom: { background: string, dataBackground: string, filler: string, handle: string },
 *   loadingOverlay: string, boxPlot: import('./tokens.js').BoxPlotTokens,
 *   sparkline: import('./tokens.js').SparklineModeTokens,
 *   gantt: import('./tokens.js').GanttTokens }>}
 *
 * @example
 * const chart = useChartTheme();
 * const option = computed(() => ({
 *   textStyle: { color: chart.value.fontColor },
 *   backgroundColor: chart.value.background,
 * }));
 * const download = () => downloadChartAsPNG(inst, 'chart.png', null, { mode: chart.value.mode });
 */
export function useChartTheme() {
  const theme = useTheme();
  return computed(() => {
    const mode = theme.global.current.value.dark ? THEME_DARK : THEME_LIGHT;
    return { ...chartTheme(mode), mode };
  });
}
